import React, { useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { FaTags, FaCode, FaRobot, FaMobileAlt, FaCloud, FaCogs, FaChartBar, FaShieldAlt, FaStore, FaSalesforce } from 'react-icons/fa';

const ServicesGrid = ({ theme = 'light', services = [] }) => {
  const isLight = theme === 'light';
  const [activeFilter, setActiveFilter] = useState('all');

  // Extract unique categories from services
  const categories = useMemo(() => {
    const uniqueCategories = new Set(
      services
        .map(service => service?.category)
        .filter(category => category && typeof category === 'string')
    );
    
    return Array.from(uniqueCategories).map(category => ({
      id: category,
      name: category ? category.charAt(0).toUpperCase() + category.slice(1) : 'Uncategorized',
      icon: getCategoryIcon(category)
    }));
  }, [services]);

  // Filter services based on active category
  const filteredServices = useMemo(() => {
    if (activeFilter === 'all') return services;
    return services.filter(service => service?.category === activeFilter);
  }, [services, activeFilter]);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { 
      opacity: 0,
      y: 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.4,
        ease: "easeOut"
      }
    }
  };

  return (
    <section className={`py-16 ${isLight ? 'bg-white' : 'bg-gray-800'}`}>
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <h2 className={`text-3xl font-bold mb-4 ${isLight ? 'text-gray-900' : 'text-white'}`}>
            Our Services
          </h2>
          <p className={`text-lg max-w-3xl mx-auto mb-8 ${isLight ? 'text-gray-600' : 'text-gray-300'}`}>
            Explore our wide range of professional services tailored to meet your specific needs
          </p>
          
          <div className="flex flex-wrap justify-center gap-2 mb-8">
            <button
              onClick={() => setActiveFilter('all')}
              className={`inline-flex items-center px-4 py-2 rounded-full text-sm font-medium transition-colors duration-200 
                ${activeFilter === 'all'
                  ? isLight 
                    ? 'bg-blue-600 text-white'
                    : 'bg-blue-500 text-white'
                  : isLight
                    ? 'bg-gray-100 text-gray-800 hover:bg-gray-200'
                    : 'bg-gray-700 text-gray-200 hover:bg-gray-600'
                }`}
            >
              <FaTags className="mr-2" />
              All Services
            </button>
            {categories.map((category) => (
              <button
                key={category.id}
                onClick={() => setActiveFilter(category.id)}
                className={`inline-flex items-center px-4 py-2 rounded-full text-sm font-medium transition-colors duration-200 
                  ${activeFilter === category.id
                    ? isLight 
                      ? 'bg-blue-600 text-white'
                      : 'bg-blue-500 text-white'
                    : isLight
                      ? 'bg-gray-100 text-gray-800 hover:bg-gray-200'
                      : 'bg-gray-700 text-gray-200 hover:bg-gray-600'
                  }`}
              >
                <span className="mr-2">{category.icon}</span>
                {category.name}
              </button>
            ))}
          </div>
        </div>
        
        <motion.div 
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          {filteredServices.map((service) => (
            <motion.div
              key={service._id}
              className={`${
                isLight 
                  ? 'bg-white border border-gray-200 shadow-sm hover:shadow' 
                  : 'bg-gray-700 border border-gray-600 shadow-sm hover:shadow'
              } rounded-lg overflow-hidden transition-all duration-300`}
              variants={itemVariants}
              whileHover={{ y: -5 }}
            >
              <Link to={`/services/${service.slug}`}>
              <div className="p-6">
                <div className={`${isLight ? 'text-blue-600' : 'text-blue-400'} mb-4 text-3xl`}>
                    {getCategoryIcon(service.category)}
                </div>
                <h3 className={`text-xl font-semibold mb-2 ${isLight ? 'text-gray-900' : 'text-white'}`}>
                  {service.title}
                </h3>
                <p className={`${isLight ? 'text-gray-600' : 'text-gray-300'}`}>
                    {service.hero?.subheadline || service.description}
                </p>
              </div>
              <div className={`px-6 py-2 ${isLight ? 'bg-gray-50' : 'bg-gray-600'}`}>
                <span className={`text-xs font-medium px-2 py-1 rounded-full ${
                  isLight 
                    ? 'bg-blue-100 text-blue-800' 
                    : 'bg-blue-900 text-blue-100'
                }`}>
                    {service.category?.toUpperCase() || 'Uncategorized'}
                </span>
              </div>
              </Link>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

// Helper function to get category icon
const getCategoryIcon = (category) => {
  switch (category) {
    case 'web':
      return <FaCode />;
    case 'mobile':
      return <FaMobileAlt />;
    case 'ai':
      return <FaRobot />;
    case 'cloud':
      return <FaCloud />;
    case 'automation':
      return <FaCogs />;
    case 'analytics':
      return <FaChartBar />;
    case 'security':
      return <FaShieldAlt />;
    case 'ecommerce':
      return <FaStore />;
    case 'crm':
      return <FaSalesforce />;
    default:
      return <FaTags />;
  }
};

export default ServicesGrid; 